import React, { createContext, FC, useContext } from 'react'
import {
  Menu,
  MenuButton,
  ButtonGroup,
  MenuList,
  MenuItem,
  useMenuState,
  ButtonProps,
  MenuProps,
  MenuListProps,
  MenuItemProps,
  IconProps,
  MenuButtonProps,
} from '@chakra-ui/react'
import { Button } from '../Button'
import { Chevron } from '../../icons'
import { BodyBackgroundNuance, useColor } from '../../theme'
import { Direction } from '../../icons/definitions'

type ButtonMenuRootProps = {
  size?: ButtonProps['size']
  placement?: MenuProps['placement']
  children?: React.ReactNode
}

type TButtonMenuCompoundType = {
  Root: FC<ButtonMenuRootProps>
  ActionsWrapper: FC<{ children?: React.ReactNode, 'data-testid'?: string }>
  Action: FC<ButtonProps>
  OptionsTrigger: FC<MenuButtonProps>
  Options: FC<MenuListProps>
  OptionItem: FC<
    Omit<MenuItemProps, 'icon'> & {
      icon: FC<IconProps>
      disabled?: boolean
    }
  >
}

const ButtonMenuContext = createContext<{
  size: ButtonProps['size']
}>({
  size: 'lg',
})

export const ButtonMenu: TButtonMenuCompoundType = {
  Root: ({ children, placement = 'bottom-end', size = 'lg' }) => {
    return (
      <ButtonMenuContext.Provider value={{ size }}>
        <Menu placement={placement}>{children}</Menu>
      </ButtonMenuContext.Provider>
    )
  },

  ActionsWrapper: ({ children }) => {
    return <ButtonGroup isAttached>{children}</ButtonGroup>
  },

  Action: (props) => {
    const { size } = useContext(ButtonMenuContext)

    return (
      <Button
        {...props}
        fontSize="md"
        iconSpacing={8}
        minW={44}
        onClick={(e) => {
          e.stopPropagation()
          props.onClick?.(e)
        }}
        size={size} />
    )
  },
  OptionsTrigger: (props) => {
    const { size } = useContext(ButtonMenuContext)
    const { isOpen } = useMenuState()
    const backgroundColor = useColor({
      nuance: BodyBackgroundNuance.Clean,
      palette: 'body',
    })

    return (
      <MenuButton
        {...props}
        as={Button}
        borderLeft="solid"
        borderLeftColor={backgroundColor}
        borderLeftWidth={2}
        data-testid="button-menu"
        onClick={(e) => {
          e.stopPropagation()
          props.onClick?.(e)
        }}
        p={2}
        size={size}
        w={14}>
        <Chevron boxSize={3} direction={isOpen ? Direction.Up : Direction.Down} />
      </MenuButton>
    )
  },
  Options: (props) => <MenuList {...props} />,
  OptionItem: ({ icon: IconComponent, disabled, onClick, ...rest }) => {
    const color = useColor({
      nuance: 5,
      palette: 'primary',
    })

    return (
      <MenuItem
        color={color}
        data-testid="menu-item"
        icon={<IconComponent color={color} height={3.5} width={3.5} />}
        iconSpacing={3.5}
        isDisabled={disabled}
        onClick={(e) => {
          e.stopPropagation()
          onClick?.(e)
        }}
        opacity={disabled ? 0.25 : 1}
        {...rest}
      />
    )
  },
}
